import { FC, memo, useCallback, useMemo } from 'react'
import { Text, Card, Image, CardProps, Flex, Box, Badge, Tooltip } from '@chakra-ui/react'
import { capitalizeString, getFormattedAvatarUrl, getFormattedAddress } from 'utils/transforms/string'
import { Avatar, TextWithCharacterLimit, Id } from 'components'
import getAssets from 'utils/assets/getAssets'
import { getInterval, formatDateToString } from 'utils/transforms/date'
import BrazilFlag from 'assets/images/flags/brazil-flag.svg'
// import baby from 'assets/images/people/baby.svg'
import { Phone, PinLocation } from 'components/icons'
import { cnhMask, cnsMask, cpfMask, ctpsMask, militaryMask, rgMask, voterCertificateMask } from 'utils/masks'
import { CITIZEN_PROFILE_ROUTES } from 'CitizenProfile/constants/routes'
import Link from 'next/link'

type Props = CardProps & {
  avatarPrefix?: 'unified_person_main' | 'person_main' | 'main'
  person: any
  personFilter?: any
  compressed?: boolean
  defaultCitizenProfilePage?: string
}

const PersonCard: FC<Props> = ({
  person,
  personFilter,
  children,
  avatarPrefix = 'person_main',
  compressed = false,
  defaultCitizenProfilePage = 'BASIC_INFORMATION',
  ...rest
}) => {
  const personFieldsValues: any = useMemo(() => {
    return {
      id: person.unified_person_id || person.person_id || person.id,
      slug: person.unified_person_slug || person.person_slug || person.slug,
      name: capitalizeString((person.unified_person_name || person.person_name || person.name) as string),
      sex: person.unified_person_sex || person.person_sex || person.sex,
      nationality: person.unified_person_nationality || person.person_nationality || person.nationality,
      kind: person.unified_person_kind || person.person_kind || person.kind,
      main_address_zip_code:
        person.unified_person_main_address_zip_code ||
        person.person_main_address_zip_code ||
        person.main_address_zip_code,
      main_phone_contact_value:
        person.unified_person_main_phone_contact_value ||
        person.main_phone_contact_value ||
        person.person_main_phone_contact_value,
      cpf_number: person.unified_person_cpf_number || person.cpf_number || person.person_cpf_number,
      cns_number: person.unified_person_cns_number || person.person_cns_number || person.cns_number,
      cnh_number: person.unified_person_cnh_number || person.person_cnh_number || person.cnh_number,
      rg_number: person.unified_person_rg_number || person.person_rg_number || person.rg_number,
      ctps_pis_number: person.unified_person_ctps_pis_number || person.ctps_pis_number || person.person_ctps_pis_number,
      vaccination_card_archive_file_name:
        person.unified_person_vaccination_card_archive_file_name ||
        person.person_vaccination_card_archive_file_name ||
        person.vaccination_card_archive_file_name,
      birth_certificate_date:
        person.unified_person_birth_certificate_date ||
        person.person_birth_certificate_date ||
        person.birth_certificate_date,
      death_certificate_date:
        person.unified_person_death_certificate_date ||
        person.person_death_certificate_date ||
        person.death_certificate_date,
      military_number: person.unified_person_military_number || person.person_military_number || person.military_number,
      voter_certificate_number:
        person.unified_person_voter_certificate_number ||
        person.person_voter_certificate_number ||
        person.voter_certificate_number,
    }
  }, [person])

  const personFilterValues = useMemo(() => {
    return {
      name: personFilter?.person_name || personFilter?.name,
      cpf_number: cpfMask(personFilter?.person_cpf_number || personFilter?.cpf_number),
    }
  }, [personFilter])

  const getPathToCitizenProfile = useCallback((citizenSlug?: string) => {
    return {
      pathname: CITIZEN_PROFILE_ROUTES.CITIZEN_PROFILE_GENERAL[defaultCitizenProfilePage],
      query: { citizenSlug },
    }
  }, [])

  const genderImage = getAssets('Gender', personFieldsValues?.sex as string)

  const documents = useMemo(() => {
    return [
      {
        name: 'CPF',
        imageName: 'cpf',
        fieldName: 'cpf_number',
        format: cpfMask,
      },
      {
        name: 'CNS',
        imageName: 'cns',
        fieldName: 'cns_number',
        format: cnsMask,
      },
      {
        name: 'CNH',
        imageName: 'cnh',
        fieldName: 'cnh_number',
        format: cnhMask,
      },
      {
        name: 'RG',
        imageName: 'rg',
        fieldName: 'rg_number',
        format: rgMask,
        boxSize: 5,
      },
      {
        name: 'Carteira de trabalho',
        imageName: 'ctps',
        fieldName: 'ctps_pis_number',
        format: ctpsMask,
        boxSize: 5,
      },
      {
        name: 'Cartão de vacina',
        imageName: 'vaccination_card',
        fieldName: 'vaccination_card_archive_file_name',
        format: (value) => value,
      },
      {
        name: 'Certidão de nascimento',
        imageName: 'birth_certificate',
        fieldName: 'birth_certificate_date',
        format: (date?: Date) => formatDateToString(date, false),
        boxSize: 5,
      },
      {
        name: 'Certidão de óbito',
        imageName: 'death_certificate',
        fieldName: 'death_certificate_date',
        format: (date?: Date) => formatDateToString(date, false),
        boxSize: 5,
      },
      {
        name: 'Certificado de alistamento militar',
        imageName: 'military',
        fieldName: 'military_number',
        format: militaryMask,
        boxSize: 5,
      },
      {
        name: 'Título eleitoral',
        imageName: 'voter_certificate',
        fieldName: 'voter_certificate_number',
        format: voterCertificateMask,
      },
    ]
  }, [])

  const PersonAvatar = () => {
    return <Avatar size="md" src={getFormattedAvatarUrl(person, avatarPrefix)} />
  }

  const PersonName = () => {
    if (personFieldsValues?.slug) {
      return (
        <Link href={getPathToCitizenProfile(personFieldsValues?.slug)} target="_blank">
          <Flex alignItems="center" justify="center">
            <TextWithCharacterLimit
              className="accessible"
              hasHighlight
              characterLimit={50}
              color="primary"
              fontSize="md"
              highlightQuery={personFilterValues.name}
              _hover={{ color: 'darkPrimary' }}
            >
              {capitalizeString(personFieldsValues?.name)}
            </TextWithCharacterLimit>
          </Flex>
        </Link>
      )
    }

    return (
      <Flex alignItems="center" justify="center">
        <TextWithCharacterLimit
          hasHighlight
          characterLimit={50}
          color="primary"
          fontSize="md"
          highlightQuery={personFilterValues.name}
        >
          {capitalizeString(personFieldsValues?.name)}
        </TextWithCharacterLimit>
      </Flex>
    )
  }

  const PersonSex = () => {
    return (
      personFieldsValues?.sex &&
      personFieldsValues?.sex !== null && <Image boxSize={5} src={genderImage} alt="Icone do gênero do cidadão" />
    )
  }

  const PersonNationality = () => {
    return (
      personFieldsValues?.nationality &&
      personFieldsValues?.nationality?.toLowerCase() !== 'Brasileiro' && (
        <Tooltip label="Brasileiro">
          <Image boxSize={6} src={BrazilFlag.src} alt="Bandeira do brasil" />
        </Tooltip>
      )
    )
  }

  const PersonAge = () => {
    return (
      personFieldsValues?.birth_certificate_date && (
        <Text variant="secondary" fontSize="sm">
          (
          {getInterval({
            start: personFieldsValues?.birth_certificate_date as Date,
          })}
          )
        </Text>
      )
    )
  }

  const PersonId = () => {
    return personFieldsValues?.id && <Id id={personFieldsValues?.id} />
  }

  const PersonKind = () => {
    return (
      personFieldsValues?.kind && (
        <Badge variant="subtle" fontSize="sm" colorScheme="green">
          {personFieldsValues?.kind}
        </Badge>
      )
    )
  }

  const VerticalDivider = () => {
    return <Box bg="borderColor" height="22px" width="2px" />
  }

  const PersonAddress = () => {
    return (
      personFieldsValues?.main_address_zip_code && (
        <Tooltip label={`Endereço: ${getFormattedAddress(person, 'main_address')}`}>
          <Box>
            <PinLocation variant="twoTone" boxSize={6} />
          </Box>
        </Tooltip>
      )
    )
  }

  const PersonPhone = () => {
    return (
      personFieldsValues?.main_phone_contact_value && (
        <Tooltip label={`Telefone: ${personFieldsValues?.main_phone_contact_value}`}>
          <Box>
            <Phone variant="twoTone" boxSize={6} />
          </Box>
        </Tooltip>
      )
    )
  }

  // const Baby = () => {
  //   return (
  //     personFieldsValues?.main_phone_contact_value && (
  //       <Tooltip label={'Primeira infância'}>
  //         <Image boxSize={6} src={baby.src} alt="Primeira infância" />
  //       </Tooltip>
  //     )
  //   )
  // }

  const PersonDocuments = () => {
    return (
      <Flex align="center" gap={1}>
        {documents.map((document) => {
          if (!personFieldsValues[document.fieldName]) return null
          const documentValue = document.format(personFieldsValues[document.fieldName])
          return (
            <Tooltip key={document.name} label={`${document.name}: ${documentValue}`}>
              <Box>
                <Image
                  boxSize={document.boxSize || 6}
                  alt={personFieldsValues[document.fieldName]}
                  src={getAssets('Document', document.imageName)}
                />
              </Box>
            </Tooltip>
          )
        })}
      </Flex>
    )
  }

  if (compressed) {
    return (
      <Card height="100%" p={2} {...rest}>
        <Flex
          direction={{
            base: 'column',
            sm: 'column',
            md: 'column',
            lg: 'row',
            xl: 'row',
            '2xl': 'row',
          }}
          align="center"
          gap={2}
          p={1}
          w="100%"
        >
          <PersonAvatar />
          <Flex flexDir="column" gap={1} w="100%">
            <Flex
              direction={{
                base: 'column',
                sm: 'column',
                md: 'row',
                lg: 'row',
                xl: 'row',
                '2xl': 'row',
              }}
              align="center"
              gap={1}
            >
              <PersonName />

              <Flex alignItems="center" justify={['center', 'center', 'start', 'start']} gap={1}>
                <PersonSex />
                <PersonNationality />
                <PersonAge />
              </Flex>

              <Flex align="center" gap={1}>
                <Flex align="center" alignItems="end" gap={1}>
                  <PersonId />
                  <PersonKind />
                  <VerticalDivider />
                  <PersonAddress />
                  <PersonPhone />
                </Flex>

                <PersonDocuments />
              </Flex>
            </Flex>
            <Box>{children}</Box>
          </Flex>
        </Flex>
      </Card>
    )
  }

  return (
    <Card height="100%" {...rest}>
      <Flex
        direction={{
          base: 'column',
          sm: 'column',
          md: 'column',
          lg: 'row',
          xl: 'row',
          '2xl': 'row',
        }}
        align="start"
        gap={2}
        p={1}
      >
        <PersonAvatar />
        <Flex flexDir="column" gap={1}>
          <Flex direction="column" align="start" gap={1}>
            <PersonName />

            <Flex alignItems="center" justify={['center', 'center', 'start', 'start']} gap={1}>
              <PersonSex />
              <PersonNationality />
              <PersonAge />
            </Flex>

            <Flex align="center" alignItems="end" gap={1}>
              <PersonId />
              <PersonKind />
              <VerticalDivider />
              <PersonAddress />
              <PersonPhone />
            </Flex>

            <PersonDocuments />
          </Flex>
        </Flex>
      </Flex>
      <Box>{children}</Box>
    </Card>
  )
}

export default memo(PersonCard)
